import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Konfiguration av nivåer, kvoter och tillåtna bevakningar
export const TIER_LIMITS = {
  trial: { summaries: 20, testRuns: 5, maxDays: 14, maxWatches: 1 }, // Uppdaterad: 20 sammanfattningar och 1 bevakning
  basic: { summaries: 775, testRuns: 50, maxDays: Infinity, maxWatches: 5 }, // 5 bevakningar för Basic
  premium: { summaries: 1000, testRuns: 200, maxDays: Infinity, maxWatches: 10 }
};

export const useUsageCheck = (userId) => {
  const [usageStats, setUsageStats] = useState({
    tier: 'trial',
    summariesGenerated: 0,
    testRunsGenerated: 0,
    limits: TIER_LIMITS.trial,
    canGenerateSummary: true,
    canRunTestSearch: true,
    canCreateWatch: true,
    isTrialExpired: false,
    isLoading: true,
    error: null
  });

  // Hämtar och beräknar användningsstatistik och begränsningar från databasen
  const fetchUsageData = useCallback(async () => {
    if (!userId) {
      setUsageStats(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setUsageStats(prev => ({ ...prev, isLoading: true, error: null }));

      const { data, error } = await supabase
        .from('user_profiles')
        .select('status, subscription_tier, summaries_generated_this_month, manual_searches_count, test_searches_this_month, created_at')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setUsageStats(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Identifiera nivå baserat på status eller subscription_tier
      const currentTier = (data.status === 'basic' || data.status === 'premium') 
        ? data.status 
        : (data.subscription_tier === 'basic' || data.subscription_tier === 'premium') 
          ? data.subscription_tier 
          : 'trial';

      const summaries = data.summaries_generated_this_month || 0;
      const testRuns = data.manual_searches_count ?? data.test_searches_this_month ?? 0;
      const limits = TIER_LIMITS[currentTier] || TIER_LIMITS.trial;

      let trialExpired = false;
      if (currentTier === 'trial' && data.created_at) {
        const createdAtDate = new Date(data.created_at);
        const currentDate = new Date();
        const daysDifference = (currentDate - createdAtDate) / (1000 * 60 * 60 * 24);

        if (daysDifference > limits.maxDays) {
          trialExpired = true;
        }
      }

      // Kontrollera om kvot finns kvar
      const hasSummariesLeft = summaries < limits.summaries;
      const canGenerate = currentTier === 'trial' 
        ? (!trialExpired && hasSummariesLeft)
        : hasSummariesLeft;

      const canTestSearch = currentTier === 'trial'
        ? (!trialExpired && testRuns < limits.testRuns)
        : (testRuns < limits.testRuns);

      setUsageStats({
        tier: currentTier,
        summariesGenerated: summaries,
        testRunsGenerated: testRuns,
        limits: limits,
        canGenerateSummary: canGenerate,
        canRunTestSearch: canTestSearch,
        isTrialExpired: trialExpired,
        isLoading: false,
        error: null
      });

    } catch (error) {
      console.error("Error fetching usage stats:", error.message);
      setUsageStats(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
        canGenerateSummary: false,
        canRunTestSearch: false
      }));
    }
  }, [userId]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  // Hjälpfunktion för att kontrollera om användaren får skapa ytterligare en bevakning
  const checkCanCreateWatch = (currentWatchesCount = 0) => {
    const limits = TIER_LIMITS[usageStats.tier] || TIER_LIMITS.trial;
    if (usageStats.tier === 'trial' && usageStats.isTrialExpired) {
      return false;
    }
    return currentWatchesCount < limits.maxWatches;
  };

  // Kontrollerar om användaren får generera innan anropet skickas
  const validateBeforeGeneration = async (type = 'summary') => {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('status, subscription_tier, summaries_generated_this_month, manual_searches_count, test_searches_this_month, created_at')
        .eq('id', userId)
        .maybeSingle();

      if (error || !data) return true;

      const currentTier = (data.status === 'basic' || data.status === 'premium') 
        ? data.status 
        : (data.subscription_tier === 'basic' || data.subscription_tier === 'premium') 
          ? data.subscription_tier 
          : 'trial';

      const limits = TIER_LIMITS[currentTier] || TIER_LIMITS.trial;

      // Om provperiod, kontrollera datum
      if (currentTier === 'trial' && data.created_at) {
        const createdAtDate = new Date(data.created_at);
        const daysDifference = (new Date() - createdAtDate) / (1000 * 60 * 60 * 24);
        if (daysDifference > limits.maxDays) return false;
      }

      // Kontrollera kvoten för både fri och betald nivå
      if (type === 'testRun') {
        const generated = data.manual_searches_count ?? data.test_searches_this_month ?? 0;
        return generated < limits.testRuns;
      } else {
        const generated = data.summaries_generated_this_month || 0;
        return generated < limits.summaries; // Bryter vid 20 för trial, 775 för basic
      }
    } catch (error) {
      console.error("Error validating usage before generation:", error.message);
      return false;
    }
  };

  // Uppdaterar användningsstatistiken säkert via server/RPC (utan direkt klient-UPDATE mot user_profiles)
  const incrementUsage = async (type = 'summary') => {
    if (!userId) return false;

    try {
      if (type === 'summary') {
        const { error: rpcError } = await supabase.rpc('increment_summary_count', {
          target_user_id: userId,
        });

        if (rpcError) {
          console.error("RPC increment_summary_count misslyckades:", rpcError.message);
          return false;
        }
      }
      // För 'testRun' sker uppräkningen av manual_searches_count på serversidan i /api/generate-summary.
      // Vi synkroniserar bara den lokala statistiken från databasen:
      await fetchUsageData();
      return true;
    } catch (error) {
      console.error(`Error incrementing ${type} usage:`, error.message);
      return false;
    }
  };

  return {
    usageStats,
    checkCanCreateWatch,
    validateBeforeGeneration,
    incrementUsage,
    refreshUsage: fetchUsageData
  };
};
